import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeStyles } from "@fluentui/react-components";
import { Add16Filled } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { checkUserPermission } from "../../../../utils";
import { UserPermissionsList } from "../../../../config";
import {
  clearAllPoliciesData,
  getPolicyListItems,
} from "../../../../redux/Case/policySlice";
import ASCButton from "../../../../components/Button";
import NoDataFound from "../../../../components/NoDataFound/noDataFound";
import CaseList from "../CaseList";
import { getCurrentStatusParam } from "../../../../shared/caseActiveStatus";

// No pre-existing route-segment convention for Policy (unlike Asset/Liability
// where these slugs are already used elsewhere in the app) — these are new.
export const POLICY_CASE_TYPE_MAP: Record<string, string> = {
  "life-assurance": "Life Assurance",
  pensions: "Pensions",
  investments: "Investments",
  "savings-plans": "Savings Plans",
  "income-protection": "Income Protection",
  "health-assurance": "Health Assurance",
  general: "General",
};

// All seven Policy sub types share a single `Status` field.
export const POLICY_HAS_STATUS_FIELD: Record<string, boolean> = {
  "life-assurance": true,
  pensions: true,
  investments: true,
  "savings-plans": true,
  "income-protection": true,
  "health-assurance": true,
  general: true,
};

type Phase =
  | "checking-current"
  | "checking-non-current"
  | "has-non-current-only"
  | "truly-empty"
  | "navigating";

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    justifyContent: "flex-end",
    margin: "10px 10px 0",
  },
});

const PolicyHomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clientId, case_type, action } = useParams<{
    clientId: string;
    case_type?: string;
    action?: string;
  }>();

  const { authUser, policies } = useSelector((state: RootState) => ({
    authUser: state.authUser,
    policies: state.policies,
  }));

  const styles = useStyles();

  useEffect(() => {
    if (clientId && !case_type) {
      navigate(`/clients/${clientId}/policies/life-assurance`, { replace: true });
    }
  }, [clientId, case_type, navigate]);

  const dbCaseType = case_type ? POLICY_CASE_TYPE_MAP[case_type] : undefined;
  const hasStatusField = case_type ? POLICY_HAS_STATUS_FIELD[case_type] : true;

  const [phase, setPhase] = useState<Phase>("checking-current");

  useEffect(() => {
    setPhase("checking-current");
  }, [case_type]);

  useEffect(() => {
    if (!clientId || !case_type) return;
    if (phase !== "checking-current" && phase !== "checking-non-current") return;

    dispatch(clearAllPoliciesData());
    dispatch(
      getPolicyListItems({
        customerId: clientId,
        caseType: dbCaseType,
        status:
          phase === "checking-current" && hasStatusField
            ? getCurrentStatusParam("policy")
            : undefined,
      })
    )?.then?.((result: any) => {
      const items = Array.isArray(result?.payload) ? result.payload : [];
      const filtered = dbCaseType
        ? items.filter((x: any) => x.case_type === dbCaseType)
        : items;

      if (phase === "checking-current") {
        if (filtered.length > 0) {
          setPhase("navigating");
        } else if (!hasStatusField) {
          setPhase("truly-empty");
        } else {
          setPhase("checking-non-current");
        }
      } else {
        setPhase(filtered.length > 0 ? "has-non-current-only" : "truly-empty");
      }
    });
  }, [clientId, case_type, dbCaseType, hasStatusField, phase, dispatch]);

  const matchingItems = useMemo(
    () =>
      dbCaseType
        ? policies.listItems.filter((item: any) => item.case_type === dbCaseType)
        : [],
    [policies.listItems, dbCaseType]
  );

  useEffect(() => {
    if (phase !== "navigating" || !case_type || matchingItems.length === 0) return;
    navigate(
      `/clients/${clientId}/policies/${case_type}/${matchingItems[0]?.policy_id}/policy/${
        action || "view"
      }`
    );
  }, [clientId, case_type, action, navigate, matchingItems, phase]);

  const handleAddNew = useCallback(() => {
    navigate(`/clients/${clientId}/policies/${case_type}/add`);
  }, [clientId, case_type, navigate]);

  if (!case_type || policies.loading) return null;
  if (
    phase === "checking-current" ||
    phase === "checking-non-current" ||
    phase === "navigating"
  ) return null;

  const addNewButton = checkUserPermission(
    authUser,
    UserPermissionsList.ALLOW_UPDATE_CLIENT
  ) && (
    <ASCButton
      shape="rounded"
      appearance="primary"
      className="asc-button-primary"
      icon={<Add16Filled />}
      size="medium"
      onClick={handleAddNew}
      label="Add New"
    />
  );

  if (phase === "has-non-current-only") {
    return (
      <div>
        <div className={styles.wrapper}>{addNewButton}</div>
        <div style={{ marginTop: "10px" }}>
          <CaseList
            currentRecord={1}
            setCurrentRecord={() => {}}
            module="policies"
            data={[]}
            activeItem={null}
            section="policies"
            tab="policy"
            hasStatusField={hasStatusField}
            showNonCurrent={false}
            onToggleNonCurrent={() => setPhase("navigating")}
            emptyMessage='No active records for current cases. Click "Non-current Cases" to view non-current records.'
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.wrapper}>{addNewButton}</div>
      <NoDataFound />
    </div>
  );
};

export default PolicyHomePage;
